import EventBus from '../utils/EventBus';
import { EEntityType } from '../types';
import { EVisibleEntityType, IVisibleState, TVisibleKey } from '../types/visible';

enum EVisibilityEventName {
  NOTIFY_CHANGE
}

type TVisibilityEvent = {
  [EVisibilityEventName.NOTIFY_CHANGE]: () => void;
};

/** 「显示全部」的目标状态：所有开关一律可见。开关初值是另一回事，见构造函数 */
export const ALL_VISIBLE_STATE: IVisibleState = {
  [EEntityType.Furniture]: true,
  [EEntityType.DoorWindow]: true,
  [EEntityType.SeparateWall]: true,
  [EEntityType.FloorPlan]: true,
  [EEntityType.AreaDimension]: true,
  [EVisibleEntityType.ColumnAndWall]: true,
  [EVisibleEntityType.RegionTextInfo]: true,
  [EVisibleEntityType.RoomTextInfo]: true,
  [EVisibleEntityType.Ruler]: true
};

/**
 * 显隐状态
 *
 * 这个 store 不依赖其它 store，也没有构造副作用，可以直接在单测中实例化。
 */
export class VisibilityStore {
  private _event = new EventBus<TVisibilityEvent>();

  /** 当前已隐藏实体 */
  private _hiddenEntitiesList: Map<EEntityType, Set<string>> = new Map();
  /** 3D 视角变化导致隐藏的墙体，与用户主动隐藏分开存放 */
  private _3dViewChangeHiddenWallIds: Set<string> = new Set();

  /** 面板各行开关的状态。继承式的键是真源，快照式的键只记录用户意图，详见 IVisibleState 注释 */
  visibleState: IVisibleState;

  /** @param initialVisibleState 开关初值。哪几行默认隐藏由 flag 决定，判断放在 stores/index.ts */
  constructor(initialVisibleState: IVisibleState = ALL_VISIBLE_STATE) {
    this.visibleState = { ...initialVisibleState };
    this._initHiddenEntitiesList();
  }

  /** 订阅显隐变化，返回取消订阅函数 */
  onChange(listener: () => void) {
    return this._event.on(EVisibilityEventName.NOTIFY_CHANGE, listener);
  }

  private _notify() {
    this._event.emit(EVisibilityEventName.NOTIFY_CHANGE);
  }

  private _initHiddenEntitiesList() {
    const keys = new Map(Object.entries(EEntityType)).values();
    const entitiesListMap = new Map<EEntityType, Set<string>>();
    for (const key of keys) {
      entitiesListMap.set(key, new Set());
    }
    this._hiddenEntitiesList = entitiesListMap;
  }

  getHiddenEntitiesIdsByEntityType(type: EEntityType): Set<string> {
    return this._hiddenEntitiesList.get(type) ?? new Set<string>();
  }

  getHiddenEntitiesIds() {
    const result: Partial<Record<EEntityType, string[]>> = {};
    for (const entityIds of this._hiddenEntitiesList) {
      const [key, list] = entityIds;
      if (key !== EEntityType.EMPTY) {
        result[key] = Array.from(list);
      }
    }

    return result;
  }

  setEntityTypeHiddenIds(type: EEntityType, ids: Set<string>) {
    this._hiddenEntitiesList.set(type, ids);
    this._notify();
  }

  /** 是否存在任何被隐藏的实体。供显隐三态推导短路使用，避免无谓的全量实体遍历 */
  hasAnyHidden(): boolean {
    for (const ids of this._hiddenEntitiesList.values()) {
      if (ids.size) return true;
    }
    return false;
  }

  /**
   * 清空全部隐藏集，返回是否真的清掉了东西
   *
   * 逐个调 `setEntityTypeHiddenIds` 会按 `EEntityType` 的数量打出几十次通知，
   * 这里合并成一次
   */
  clearAllHiddenEntities(): boolean {
    let changed = false;
    for (const [type, ids] of this._hiddenEntitiesList) {
      if (!ids.size) continue;
      changed = true;
      this._hiddenEntitiesList.set(type, new Set());
    }
    if (changed) this._notify();
    return changed;
  }

  setVisibleState(type: TVisibleKey, status: boolean) {
    this.visibleState = {
      ...this.visibleState,
      [type]: status
    };
    this._notify();
  }

  /** 把所有开关复位为可见，只发一次通知 */
  resetVisibleState() {
    this.visibleState = { ...ALL_VISIBLE_STATE };
    this._notify();
  }

  /**
   * 3D 视角变化导致的墙体隐藏
   *
   * 不发通知：它随相机变化高频更新，而显隐面板的状态不依赖它，通知只会带来无谓的重算
   */
  set3dViewChangeHiddenWallIds(ids: Set<string>) {
    this._3dViewChangeHiddenWallIds = ids;
  }

  get3dViewChangeHiddenWallIds() {
    return this._3dViewChangeHiddenWallIds;
  }
}
